import { useCallback, useState } from 'react';
import type { ITakeOptions } from 'utils/screenshot/screenshot';
import { take as takeScreenshot } from 'utils/screenshot/screenshot';

interface UseScreenshotIdle {
  state: 'idle';
  take(): void;
}

interface UseScreenshotPending {
  state: 'pending';
}

interface UseScreenshotError {
  state: 'error';
  error: string;
}

interface UseScreenshotDone {
  state: 'done';
  image: HTMLCanvasElement;
  reset(): void;
  revert(): void;
  take(): void;
  update(screenshot: HTMLCanvasElement): void;
}

export type UseScreenshotValue = UseScreenshotOptions &
  (UseScreenshotIdle | UseScreenshotPending | UseScreenshotDone | UseScreenshotError);

function shouldIgnoreElement(element: Element): boolean {
  const { classList } = element;
  return Boolean(
    (classList &&
      (classList.contains('life-ring-modal-component') ||
        classList.contains('modal') ||
        classList.contains('modal-backdrop') ||
        classList.contains('sp-tooltip'))) ||
    element.getAttribute('role') === 'dialog',
  );
}

export interface UseScreenshotOptions {
  shouldIgnoreElementOverride?: (element: Element) => boolean;
}
export function useScreenshot({ shouldIgnoreElementOverride }: UseScreenshotOptions): UseScreenshotValue {
  const [isIdle, setIdle] = useState(true);
  const [originalScreenshot, setOriginalScreenshot] = useState<HTMLCanvasElement>();
  const [screenshot, setScreenshot] = useState<HTMLCanvasElement>();
  const [error, setError] = useState<string>();

  const reset = useCallback(() => {
    setOriginalScreenshot(undefined);
    setScreenshot(undefined);
  }, []);

  const revert = useCallback(() => {
    setScreenshot(originalScreenshot);
  }, [originalScreenshot]);

  const take = useCallback(() => {
    setIdle(false);

    const options: ITakeOptions = {
      element: document.body,
      shouldIgnoreElement: el =>
        shouldIgnoreElement(el) && (shouldIgnoreElementOverride === undefined || shouldIgnoreElementOverride(el)),
    };

    takeScreenshot(options).then(
      (newScreenshot: HTMLCanvasElement) => {
        setOriginalScreenshot(newScreenshot);
        setScreenshot(newScreenshot);
      },
      e => {
        setError(e);
        setOriginalScreenshot(undefined);
        setScreenshot(undefined);
      },
    );
  }, [shouldIgnoreElementOverride]);

  const update = useCallback((newScreenshot: HTMLCanvasElement) => {
    setScreenshot(newScreenshot);
  }, []);

  if (isIdle) {
    return { state: 'idle', take };
  }
  if (error !== undefined) {
    return {
      state: 'error',
      error,
    };
  }
  if (screenshot === undefined) {
    return { state: 'pending' };
  }

  return {
    state: 'done',
    image: screenshot,
    reset,
    revert,
    take,
    update,
  };
}
